import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  searchOrganizations,
  AddOrganizationRelationship,
  updateOrganizationRelationship,
} from '../../../containers/AdminPanel/api';
import { hideDialogueBox } from '../../../components/DialogueBox/actions';
import Icon from '../../../icons';
import OrganisationInfo from '../OrgInfo';
import { STATUS_TEXT_MAP } from './constants';
import { getOrgId } from '@/utils/authManager';
import { selectRelationshipsData } from '../../../containers/AdminPanel/features/relationships/relationshipsSelectors';

const CURRENT_ORG_ID = Number(getOrgId());

/**
 * Extracts a consistent ID from various shapes of organization/relationship row objects.
 * @param {object} row - The row data.
 * @returns {number|string|null} The extracted ID.
 */
const getRowId = (row) => {
  if (!row) return null;
  return row.id || row.OrganizationId || row.assignee || (row?.destination_org?.id) || null;
};

/**
 * Resolves the relationship context for a given row, ensuring `status` is explicitly set (even to null).
 * @param {object} row - The raw relationship row.
 * @returns {object} The enriched row object.
 */
const resolveRelationshipContext = (row = {}) => ({
  ...row,
  status: row?.status ?? null,
});

/**
 * Extracts and normalizes the status string from a row object.
 * @param {object} row - The relationship data row.
 * @returns {string|null} The normalized status or null.
 */
const getNormalizedStatus = (row = {}) => {
  return row?.status ? String(row.status).toUpperCase() : null;
};

/**
 * Builds the standard payload for creating or updating a relationship.
 * @param {object} row - The relationship data row.
 * @param {string} status - The target status.
 * @returns {object} The formatted API payload.
 */
const buildPayload = (row, status) => {
  const destinationOrgId = getRowId(row);
  return {
    owner: CURRENT_ORG_ID,
    status,
    assignee: destinationOrgId,
  };
};

/**
 * Maps the clicked button label to the corresponding API status value.
 * @param {string} label - The action button label.
 * @param {object} row - The relationship data row.
 * @returns {string} The target relationship status.
 */
const resolveActionStatus = (label, row = {}) => {
  const normalizedStatus = getNormalizedStatus(row) || '';
  if (label === 'Exclude' && normalizedStatus.includes('PENDING')) {
    return 'EXCLUDED_SEARCH';
  }
  if(label === 'Send Request'){
    return 'PENDING'
  }
  return STATUS_TEXT_MAP[label] || label.toUpperCase();
};
/**
 * Determines which action buttons should be available based on the relationship status.
 * @param {string} status - The normalized relationship status.
 * @returns {Array<string>} List of available action labels.
 */
const getActionLabelsByStatus = (status) => {
  const normalizedStatus = (status || '').toUpperCase();

  if (normalizedStatus === 'PENDING_TO_ACCEPT') {
    return ['Exclude'];
  }
  if (normalizedStatus.startsWith('EXCLUDED')) {
    return ['Include'];
  }
  if (['PENDING', 'PENDING_FROM_RECEIVER'].includes(normalizedStatus)) {
    return ['Accept', 'Ignore', 'Exclude'];
  }
  if (normalizedStatus.startsWith('IGNORED')) {
    return ['Accept', 'Exclude'];
  }
  return ['Send Request'];
};

/**
 * Modal component to search for organizations and add/manage relationships.
 */
const AddRelationshipModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const relationships = useSelector(selectRelationshipsData);

  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState([]);
  const [selectedRelationship, setSelectedRelationship] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  /**
   * Closes the modal and clears any pending debounced searches.
   */
  const closeModal = () => {
    clearTimeout(debounceRef.current);
    if (onClose) onClose();
  };

  /**
   * Handles user input in the search field and triggers API search.
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const handleChange = (e) => {
    const value = e.target.value;
    setKeyword(value);
    setSelectedRelationship(null);
    setError('');

    clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setResults([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await searchOrganizations(value.trim());
        const data = Array.isArray(response?.organizations) ? response.organizations : [];
        setResults(data);
      } catch (err) {
        setError('Failed to search organizations.');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  /**
   * Clears the current search results and keyword.
   * @param {React.MouseEvent} [e]
   */
  const clearSearch = (e) => {
    if (e) e.preventDefault();
    clearTimeout(debounceRef.current);
    setKeyword('');
    setResults([]);
    setSelectedRelationship(null);
    setError('');
    if (inputRef?.current?.focus) {
      inputRef.current.focus();
    }
  };

  /**
   * Handles selecting an organization from the search results list.
   * Cross-references with existing relationships to apply correct status.
   * @param {object} row - The selected organization/relationship.
   */
  const handleSelect = (row) => {
    const orgId = getRowId(row);
    const existingRel = (relationships || []).find((rel) => rel.OrganizationId === orgId);
    const enrichedRow = resolveRelationshipContext({
      ...row,
      status: existingRel?.RequestStatus || row.status || null,
      RelationshipId: existingRel?.RelationshipId || null,
    });
    setSelectedRelationship(enrichedRow);
    setKeyword('');
    setResults([]);
    setError('');
  };

  const selectedStatus = getNormalizedStatus(selectedRelationship);
  const selectedActions = selectedRelationship
    ? getActionLabelsByStatus(selectedStatus)
    : ['Send Request'];

  /**
   * Triggers the API call to update or add a relationship.
   * @param {string} label - The action label clicked (e.g., 'Send Request').
   * @param {object} row - The target relationship data.
   */
  const handleAction = async (label, row) => {
    const actionStatus = resolveActionStatus(label, row);
    const payload = buildPayload(row, actionStatus);

    try {
      if (label === 'Send Request') {
        await AddOrganizationRelationship(payload);
      } else {
        await updateOrganizationRelationship(payload);
      }
      dispatch(hideDialogueBox());
    } catch (err) {
      setError('Failed to update relationship.');
    }
  };

  const renderedResults = results.map(resolveRelationshipContext);
  const hasSearchResults = !selectedRelationship && !loading && renderedResults.length > 0;

  return (
    <React.Fragment>
      <div className="modalHeader">
        <h1>Add Relationship</h1>
      </div>

      <div className="modalBody">
        <div className="search">
          <div className="formGroup mrNone">
            <div className='search rowFlex'>
              <input
                ref={inputRef}
              type="text"
              autoFocus
              value={keyword}
              onChange={handleChange}
              placeholder="Search by organisation name"
              />
              <div className="search__icon">
                <ul className="rowFlex">
                  <li>
                    {keyword ? (
                      <a href="#" onClick={clearSearch} aria-label="Clear search">
                        <Icon name="close" className="icon iconSmall" />
                      </a>
                    ) : (
                      <a href="#" onClick={(e) => e.preventDefault()}>
                        <Icon name="search" className="icon searchIcon" />
                      </a>
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {hasSearchResults && (
            <div className="search-listing" >
              <ul>
                {renderedResults.map((row, index) => (
                  <li key={getRowId(row) || index} onClick={() => handleSelect(row)}>
                    <OrganisationInfo row={row} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {selectedRelationship && (
            <div className="search-listing search-listing__selected" >
              <ul>
                <li onClick={() => setSelectedRelationship(null)} >
                  <OrganisationInfo row={selectedRelationship} showSelectedTick />
                </li>
              </ul>
            </div>
          )}

          {!selectedRelationship && renderedResults.length === 0 && !error && (
            <div className='noDataFound search-listing__info'>
              <p>Enter atleast 3 charater to view result </p>
            </div>
          )}
          
          {error && <div>{error}</div>}
        </div>
      </div>

      <div className="modalFooter">
        {selectedActions.map((label) => {
          const buttonClass = label === 'Exclude' || label === 'Ignore'
            ? 'btn-cc__danger'
            : 'btn-cc__primary';
          const isDisabled = !selectedRelationship;
          const className = isDisabled ? `${buttonClass} disabled` : buttonClass;

          return (
            <button
              key={label}
              type="button"
              className={className}
              disabled={isDisabled}
              onClick={() => selectedRelationship && handleAction(label, selectedRelationship)}
            >
              {label}
            </button>
          );
        })}
        <button type="button" className="btn-cc__link" onClick={closeModal}>
          Cancel
        </button>
      </div>
    </React.Fragment>
  );
};

AddRelationshipModal.propTypes = {
  onClose: PropTypes.func,
};

export default AddRelationshipModal;
